import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faSpinner,
  faArrowAltCircleUp,
  faTrash,
  faCircleArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../../services/Interceptor";
import {
  sendMeasurements,
  getMeasurements,
} from "../../../../services/CombineService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadMeasurementVideo,
  getMeasurementFullVideoAction,
  deleteMeasurementFullVideoAction,
} from "../../../../store/MeasurementActions";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import VideoPlayer from "../../../../components/VideoPlayer";
import { Link } from "react-router-dom";
import ConfirmModal from "../../../../components/ConfirmModel";
import ReactPlayer from "react-player";
import {
  setMeasurementFullVideo,
  setMeasurementVideos,
} from "../../../../store/slices/measurementSlice";
import LoadingSpinner from "../../../../components/LoadingSpinner";

export default function Measurements({ eventId, athlete }) {
  const apiInstance = useAxiosInstance();
  const [data, setData] = useState({
    heightFt: 0,
    heightIn: 0,
    weight: 0,
    wingSpanFt: 0,
    wingSpanIn: 0,
    hand: 0,
    foot: 0,
    heightScratch: false,
    heightScratchDeduction: "",
    wingSpanScratch: false,
    wingSpanScratchDeduction: "",
    handScratch: false,
    handScratchDeduction: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const posToast = { position: "top-right" };
  const formMeasurements = useRef();
  const [loading, setLoading] = useState(false);
  const measurementFull = useRef();
  const dispatch = useDispatch();
  const auth = useAuthUser();
  const r_measurement = useSelector((state) => state.measurement);
  const [done, setDone] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // handleChange updated to support numeric min clamping and controlled values
  const handleChange = (event) => {
    const { name, value, type, min } = event.target;

    if (type === "number") {
      // allow clearing the field while typing
      if (value === "") {
        setData((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      const num = Number(value);
      if (Number.isNaN(num)) return;

      const minNum =
        typeof min !== "undefined" && min !== "" ? Number(min) : undefined;
      const final =
        typeof minNum === "number" && !Number.isNaN(minNum)
          ? Math.max(num, minNum)
          : num;

      setData((prev) => ({ ...prev, [name]: final }));
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Scratch checkboxes: no default deduction, and unchecking clears it
  const handleScratchChange = (event) => {
    const { name, checked } = event.target;
    setData((prev) => ({
      ...prev,
      [name]: checked,
      [`${name}Deduction`]: "",
    }));
  };

  useEffect(() => {
    const getCombineMeasurements = async () => {
      if (formMeasurements.current) formMeasurements.current.reset();
      const response = await getMeasurements(
        apiInstance,
        eventId,
        athlete.userId,
      );
      setData({
        eventId: eventId,
        userId: athlete.userId,
        name: athlete.name,
        age: athlete.age,
        // Inputs hold what was measured; the back end applies the scratch deduction
        heightFt: response.measuredHeightFt ?? 0,
        heightIn: response.measuredHeightIn ?? 0,
        weight: response.weight ?? 0,
        wingSpanFt: response.measuredWingSpanFt ?? 0,
        wingSpanIn: response.measuredWingSpanIn ?? 0,
        hand: response.measuredHand ?? 0,
        foot: response.foot ?? 0,
        heightScratch: response.heightScratchDeduction != null,
        heightScratchDeduction: response.heightScratchDeduction ?? "",
        wingSpanScratch: response.wingSpanScratchDeduction != null,
        wingSpanScratchDeduction: response.wingSpanScratchDeduction ?? "",
        handScratch: response.handScratchDeduction != null,
        handScratchDeduction: response.handScratchDeduction ?? "",
        photoUrl: response.photoUrl,
        id: response.id,
      });
      setDone(true);
    };
    getCombineMeasurements();

    return () => {
      setDone(false);
      setData({});
    };
  }, [eventId, athlete.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(setMeasurementFullVideo({}));
    dispatch(setMeasurementVideos([]));

    if (data.id && done) {
      dispatch(getMeasurementFullVideoAction(apiInstance, data.id));
    }
  }, [data.id, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const toNumberOrZero = (v) => {
    if (v === "" || v === null || typeof v === "undefined") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const sanitizeForApi = ({
    heightScratch,
    wingSpanScratch,
    handScratch,
    ...m
  }) => ({
    ...m,
    heightFt: toNumberOrZero(m.heightFt),
    heightIn: toNumberOrZero(m.heightIn),
    weight: toNumberOrZero(m.weight),
    wingSpanFt: toNumberOrZero(m.wingSpanFt),
    wingSpanIn: toNumberOrZero(m.wingSpanIn),
    hand: toNumberOrZero(m.hand),
    foot: toNumberOrZero(m.foot),
    heightScratchDeduction: heightScratch
      ? toNumberOrZero(m.heightScratchDeduction)
      : null,
    wingSpanScratchDeduction: wingSpanScratch
      ? toNumberOrZero(m.wingSpanScratchDeduction)
      : null,
    handScratchDeduction: handScratch
      ? toNumberOrZero(m.handScratchDeduction)
      : null,
  });

  const scratchFields = [
    {
      label: "Height",
      scratch: "heightScratch",
      measured: (m) =>
        toNumberOrZero(m.heightFt) * 12 + toNumberOrZero(m.heightIn),
    },
    {
      label: "Wingspan",
      scratch: "wingSpanScratch",
      measured: (m) =>
        toNumberOrZero(m.wingSpanFt) * 12 + toNumberOrZero(m.wingSpanIn),
    },
    {
      label: "Hand",
      scratch: "handScratch",
      measured: (m) => toNumberOrZero(m.hand),
    },
  ];

  // Returns a message for the first scratch without a valid deduction
  const getScratchError = (m) => {
    for (const { label, scratch, measured } of scratchFields) {
      if (!m[scratch]) continue;
      const deduction = toNumberOrZero(m[`${scratch}Deduction`]);
      if (deduction <= 0) {
        return `${label}: the scratch deduction is required and must be greater than 0.`;
      }
      if (deduction >= measured(m)) {
        return `${label}: the scratch deduction must be less than the measurement.`;
      }
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const scratchError = getScratchError(data);
    if (scratchError) {
      toast.error(scratchError, posToast);
      return;
    }
    setBtnLoading(true);
    try {
      const payload = sanitizeForApi(data);
      await sendMeasurements(apiInstance, payload);
      toast.success("Measurements saved successfully!", posToast);
    } catch (err) {
      toast.error("Error saving measurements", posToast);
    } finally {
      setBtnLoading(false);
    }
  };

  // Returns false, without saving, when a scratch is invalid so the upload stops
  const uploadFromFieldSet = () => {
    const scratchError = getScratchError(data);
    if (scratchError) {
      toast.error(scratchError, posToast);
      return false;
    }
    const payload = sanitizeForApi(data);
    sendMeasurements(apiInstance, payload);
    return true;
  };

  const handleUploadVideo = async () => {
    const scratchError = getScratchError(data);
    if (scratchError) {
      toast.error(scratchError, posToast);
      // let the same file be picked again once the scratch is fixed
      measurementFull.current.value = "";
      return;
    }
    setLoading(true);
    const response = await sendMeasurements(apiInstance, sanitizeForApi(data));
    if (response.id) {
      const file = measurementFull.current.files[0];

      if (file) {
        const fileType = file.type;
        const isVideo = fileType.startsWith("video/");

        if (!isVideo) {
          setLoading(false);
          toast.error(
            "The file format is invalid. Please select a video file.",
          );
          return;
        }
        const formData = new FormData();
        formData.append("file", file);
        dispatch(
          uploadMeasurementVideo(
            apiInstance,
            formData,
            setLoading,
            auth.user.id,
            eventId,
            athlete.userId,
            6,
            setUploadProgress,
          ),
        )
          .then(() => {
            toast.success("Video uploaded successfully!");
            setUploadProgress(0);

            dispatch(getMeasurementFullVideoAction(apiInstance, response.id));
          })
          .catch(() => {
            toast.error("Error uploading video.");
          });
      }
    }
  };

  const requestDeleteVideo = (measurementVideoId) => {
    setVideoToDelete(measurementVideoId);
    setShowConfirmModal(true);
  };

  const handleDeleteVideo = () => {
    if (videoToDelete) {
      setLoading(true);
      dispatch(
        deleteMeasurementFullVideoAction(
          apiInstance,
          videoToDelete,
          setLoading,
          0,
        ),
      )
        .then(() => {
          toast.success("Video deleted successfully!");
          dispatch(getMeasurementFullVideoAction(apiInstance, data.id));
        })
        .catch(() => {
          toast.error("Error deleting video.");
        })
        .finally(() => {
          setLoading(false);
          setShowConfirmModal(false);
          setVideoToDelete(null);
        });
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setVideoToDelete(null);
  };

  return (
    <>
      {loading && (
        <div className="w-[46%] pt-10 h-full z-20 bg-white opacity-60 fixed">
          <LoadingSpinner />
        </div>
      )}
      <div className="relative w-full">
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="w-full pr-2 mb-2">
            <div className="bg-gray-300 rounded-full h-2 w-full">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-600 mt-1 text-right pr-1">
              Uploading: {uploadProgress}%
            </p>
          </div>
        )}

        <>
          {r_measurement.measurementFullVideo.filePath ? (
            <div className="mt-2 mb-5 w-4/5 mx-auto">
              <div className="w-full h-[200px] bg-black">
                <ReactPlayer
                  controls
                  light={true}
                  src={r_measurement.measurementFullVideo.filePath}
                  playsInline={true}
                  height={200}
                  width="100%"
                />
              </div>
              <button
                type="button"
                onClick={() =>
                  requestDeleteVideo(r_measurement.measurementFullVideo.id)
                }
                className="block w-full bg-red-500 text-white text-center cursor-pointer hover:opacity-95 py-1 active:scale-95"
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          ) : (
            <div className="my-3 xl:my-6">
              <div className="flex items-center justify-items-end">
                <label
                  htmlFor="Upload"
                  className=" border border-blue-700 border-dashed rounded-lg cursor-pointer px-4 py-2 w-full bg-gray-100"
                >
                  <div className="flex flex-col items-center">
                    <FontAwesomeIcon
                      icon={faArrowAltCircleUp}
                      size="xl"
                      className="text-gray-600"
                    />
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-normal">Click to upload Video</span>
                    </p>
                  </div>
                  <input
                    id="Upload"
                    type="file"
                    className="hidden"
                    ref={measurementFull}
                    onChange={handleUploadVideo}
                    accept="video/*"
                  />
                </label>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} ref={formMeasurements}>
            <FieldSet
              title="Weight"
              athleteId={athlete.userId}
              userId={auth.user.id}
              measurementType={1}
              eventId={eventId}
              video={r_measurement.measurementVideos.filter(
                (x) => x.measurementType === "Weight",
              )}
              deleteVideo={requestDeleteVideo}
              sendMeasurements={uploadFromFieldSet}
            >
              <InputField
                type="number"
                id="weight"
                name="weight"
                placeholder="0.000"
                handleChange={handleChange}
                value={data.weight ?? 0}
                step={0.001}
                min={0}
              />
            </FieldSet>

            <FieldSet
              title="Height"
              athleteId={athlete.userId}
              userId={auth.user.id}
              measurementType={2}
              eventId={eventId}
              deleteVideo={requestDeleteVideo}
              video={r_measurement.measurementVideos.filter(
                (x) => x.measurementType === "Height",
              )}
              sendMeasurements={uploadFromFieldSet}
            >
              <div className="flex space-x-3">
                <InputField
                  type="number"
                  id="heightFt"
                  name="heightFt"
                  placeholder="ft"
                  handleChange={handleChange}
                  value={data.heightFt ?? 0}
                  step={1}
                  min={0}
                />
                <InputField
                  type="number"
                  id="heightIn"
                  name="heightIn"
                  placeholder="in"
                  handleChange={handleChange}
                  value={data.heightIn ?? 0}
                  step={0.001}
                  min={0}
                />
              </div>
              <ScratchField
                name="heightScratch"
                checked={data.heightScratch}
                deduction={data.heightScratchDeduction}
                handleScratchChange={handleScratchChange}
                handleChange={handleChange}
              />
            </FieldSet>

            <FieldSet
              title="Wingspan"
              athleteId={athlete.userId}
              userId={auth.user.id}
              measurementType={3}
              eventId={eventId}
              deleteVideo={requestDeleteVideo}
              video={r_measurement.measurementVideos.filter(
                (x) => x.measurementType === "Wingspan",
              )}
              sendMeasurements={uploadFromFieldSet}
            >
              <div className="flex space-x-3">
                <InputField
                  type="number"
                  id="wingSpanFt"
                  name="wingSpanFt"
                  placeholder="ft"
                  handleChange={handleChange}
                  value={data.wingSpanFt ?? 0}
                  step={1}
                  min={0}
                />
                <InputField
                  type="number"
                  id="wingSpanIn"
                  name="wingSpanIn"
                  placeholder="in"
                  handleChange={handleChange}
                  value={data.wingSpanIn ?? 0}
                  step={0.001}
                  min={0}
                />
              </div>
              <ScratchField
                name="wingSpanScratch"
                checked={data.wingSpanScratch}
                deduction={data.wingSpanScratchDeduction}
                handleScratchChange={handleScratchChange}
                handleChange={handleChange}
              />
            </FieldSet>

            <FieldSet
              title="Hand"
              athleteId={athlete.userId}
              userId={auth.user.id}
              measurementType={4}
              eventId={eventId}
              deleteVideo={requestDeleteVideo}
              video={r_measurement.measurementVideos.filter(
                (x) => x.measurementType === "Hand",
              )}
              sendMeasurements={uploadFromFieldSet}
            >
              <InputField
                type="number"
                id="hand"
                name="hand"
                placeholder="in"
                handleChange={handleChange}
                value={data.hand ?? 0}
                step={0.001}
                min={0}
              />
              <ScratchField
                name="handScratch"
                checked={data.handScratch}
                deduction={data.handScratchDeduction}
                handleScratchChange={handleScratchChange}
                handleChange={handleChange}
              />
            </FieldSet>

            <FieldSet
              title="Foot"
              athleteId={athlete.userId}
              userId={auth.user.id}
              measurementType={5}
              eventId={eventId}
              deleteVideo={requestDeleteVideo}
              video={r_measurement.measurementVideos.filter(
                (x) => x.measurementType === "Foot",
              )}
              sendMeasurements={uploadFromFieldSet}
            >
              <InputField
                type="number"
                id="foot"
                name="foot"
                placeholder="size"
                handleChange={handleChange}
                value={data.foot ?? 0}
                step={0.001}
                min={0}
              />
            </FieldSet>
            <div className="w-auto">
              {btnLoading ? (
                <button
                  type="button"
                  className="uppercase h-[40px] px-2 text-white bg-red-300 cursor-not-allowed rounded ml-4 my-auto"
                >
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Saving Changes
                </button>
              ) : (
                <button className="uppercase py-2 px-2 text-white bg-red-500 rounded hover:bg-red-700">
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Save / Update Changes
                </button>
              )}
            </div>
          </form>
        </>
      </div>
      <ToastContainer />
      <ConfirmModal
        show={showConfirmModal}
        message="Are you sure you want to delete this video?"
        onConfirm={handleDeleteVideo}
        onCancel={handleCancelDelete}
      />
    </>
  );
}

const FieldSet = ({
  children,
  title,
  eventId,
  athleteId,
  userId,
  measurementType,
  video,
  deleteVideo,
  sendMeasurements,
}) => {
  const fileInputRef = useRef();
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (event) => {
    if (!sendMeasurements()) {
      // let the same file be picked again once the scratch is fixed
      event.target.value = "";
      return;
    }
    setLoading(true);
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      if (!fileType.startsWith("video/")) {
        toast.error("Please select a valid video file.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        await dispatch(
          uploadMeasurementVideo(
            apiInstance,
            formData,
            setLoading,
            userId,
            eventId,
            athleteId,
            measurementType,
            setUploadProgress,
          ),
        );
        toast.success("Video uploaded successfully!");
      } catch (error) {
        toast.error("Error uploading the video.");
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="border border-blue-700 rounded px-4 py-3 mb-3">
      <div className="flex items-center justify-between">
        <div className="font-normal">{title}</div>
        <div className="flex-grow mx-4">{children}</div>
        {video && video.length > 0 && video[0].filePath ? (
          <div className="bg-transparent flex justify-between items-center">
            <VideoPlayer src={video[0].filePath} id={video[0].id} />
            <Link className="mx-3" onClick={() => deleteVideo(video[0].id, 0)}>
              <FontAwesomeIcon
                icon={faTrash}
                size="sm"
                className="text-gray-500"
              />
            </Link>
          </div>
        ) : (
          <div className="flex justify-start">
            <label className="border border-blue-700 border-dashed rounded-lg cursor-pointer px-1 py-1 xl:px-4 xl:py-2 w-full bg-gray-100 flex flex-col items-center">
              <FontAwesomeIcon
                icon={faCircleArrowUp}
                size="lg"
                className="text-gray-500"
              />
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-normal">Click to upload Video</span>
              </p>
              <input
                type="file"
                accept="video/*"
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
            </label>
          </div>
        )}
      </div>
      {loading && (
        <>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full pr-2 mb-2">
              <div className="bg-gray-300 rounded-full h-2 w-full">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1 text-right pr-1">
                Uploading: {uploadProgress}%
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ScratchField = ({
  name,
  checked,
  deduction,
  handleScratchChange,
  handleChange,
}) => (
  <div className="flex items-center space-x-3 mt-2">
    <label className="flex items-center text-sm text-gray-600">
      <input
        type="checkbox"
        id={name}
        name={name}
        className="mr-2"
        checked={!!checked}
        onChange={handleScratchChange}
      />
      Scratch
    </label>
    <InputField
      type="number"
      id={`${name}Deduction`}
      name={`${name}Deduction`}
      placeholder="in"
      handleChange={handleChange}
      value={deduction}
      step={0.001}
      min={0}
      disabled={!checked}
      className="disabled:bg-gray-200 disabled:cursor-not-allowed"
    />
  </div>
);

const InputField = ({
  type,
  id,
  name,
  placeholder,
  handleChange,
  value,
  step,
  min,
  required = false,
  className = "",
  ...rest
}) => {
  // prevent typing invalid chars for number inputs (e, +, -)
  const handleKeyDown = (e) => {
    if (type === "number" && ["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  // prevent scroll wheel from changing number input value
  const handleWheel = (e) => {
    if (type === "number") e.currentTarget.blur();
  };

  return (
    <input
      type={type}
      id={id}
      className={`bg-gray-50 border border-gray-600 text-gray-900 text-sm rounded-lg block w-16 md:w-32 p-2.5 ${className}`}
      placeholder={placeholder}
      name={name}
      onChange={handleChange}
      value={typeof value === "undefined" || value === null ? "" : value}
      step={step}
      min={min}
      required={required}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      {...rest}
    />
  );
};
